import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { Photos, Telemetry, Survey, fetchPhotoBlob, saveBlob, photoSrc } from "../lib/api";
import { ImportPhotos } from "../components/ImportPhotos";
import { TelemetryPanel } from "../components/TelemetryPanel";
import { LocalStore, padSeq } from "../lib/storage";
import { dataUrlToBlob } from "../lib/exif";
import {
  Camera as CameraIcon,
  Trash,
  ArrowSquareOut,
  X,
  CheckSquare,
  Square,
  DownloadSimple,
  ListChecks,
  PencilSimple,
  FloppyDisk,
  MapPin,
  MapTrifold,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function GalleryPage() {
  const nav = useNavigate();
  const [active] = useState(LocalStore.getActiveSession());
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // {id, filename, image_data}
  const [filterTag, setFilterTag] = useState("ALL");
  const [filterSeg, setFilterSeg] = useState("ALL");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Display-override editor state (UI-only, never mutates ingestion identity).
  const [renaming, setRenaming] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [renameTags, setRenameTags] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  useEffect(() => {
    if (!active) {
      setLoading(false);
      return;
    }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const list = await Photos.list(active.id);
      setPhotos(list);
    } catch {
      toast.error("Failed to load photos");
    } finally {
      setLoading(false);
    }
  };

  const groups = useMemo(() => {
    const m = new Map();
    const byTag = filterTag === "ALL" ? photos : photos.filter((p) => (p.tags || "untagged") === filterTag);
    const filtered = filterSeg === "ALL" ? byTag : byTag.filter((p) => String(p.segment ?? "none") === filterSeg);
    for (const p of filtered) {
      const key = p.tags || "untagged";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(p);
    }
    return Array.from(m.entries());
  }, [photos, filterTag, filterSeg]);

  const segments = useMemo(() => {
    const s = new Set();
    for (const p of photos) if (p.segment != null) s.add(p.segment);
    return Array.from(s).sort((a, b) => a - b);
  }, [photos]);
  const gpsCount = useMemo(() => photos.filter((p) => p.gps?.lat != null).length, [photos]);

  const allTags = useMemo(() => {
    const s = new Set();
    for (const p of photos) s.add(p.tags || "untagged");
    return Array.from(s);
  }, [photos]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const selectAllVisible = () => {
    const ids = new Set();
    for (const [, items] of groups) items.forEach((p) => ids.add(p.id));
    setSelected(ids);
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} photo(s)?`)) return;
    setBulkBusy(true);
    try {
      for (const id of Array.from(selected)) {
        await Photos.remove(active.id, id);
      }
      setPhotos((prev) => prev.filter((p) => !selected.has(p.id)));
      toast.success(`Deleted ${selected.size}`);
      exitSelectMode();
    } catch {
      toast.error("Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDownload = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder(active.name.replace(/[^a-zA-Z0-9-_]+/g, "-")) || zip;
      for (const id of Array.from(selected)) {
        const full = await Photos.get(active.id, id);
        folder.file(full.filename, full.image_data ? dataUrlToBlob(full.image_data) : await fetchPhotoBlob(id));
      }
      // Geospatial bundle: GeoJSON of selected photos (+ flight path) when any has GPS.
      if (Array.from(selected).some((id) => photos.find((p) => p.id === id)?.gps?.lat != null)) {
        try {
          const geo = await Telemetry.geojson(active.id);
          geo.features = geo.features.filter((f) => f.geometry.type !== "Point" || selected.has(f.properties.id));
          folder.file("photos.geojson", JSON.stringify(geo, null, 2));
          folder.file("survey.kml", await Survey.kml(active.id));
        } catch { /* export still succeeds without the sidecars */ }
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.name.replace(/[^a-zA-Z0-9-_]+/g, "-")}_selection.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selected.size}`);
    } catch {
      toast.error("Download failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const openPreview = async (p) => {
    try {
      const full = await Photos.get(active.id, p.id);
      setPreview(full);
      setRenaming(false);
      setRenameName(full.display_name || "");
      setRenameTags(
        Array.isArray(full.display_tags) ? full.display_tags.join(", ") : ""
      );
    } catch {
      toast.error("Failed to load image");
    }
  };

  const closePreview = () => {
    setPreview(null);
    setRenaming(false);
  };

  const saveRename = async () => {
    if (!preview) return;
    setRenameBusy(true);
    try {
      const tagsArr = renameTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      // Send "" explicitly to clear display_name when user empties the field.
      const patched = await Photos.patchDisplay(preview.id, {
        display_name: renameName,
        display_tags: tagsArr.length ? tagsArr : [],
      });
      setPreview((cur) => (cur ? { ...cur, ...patched } : cur));
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === preview.id
            ? { ...p, display_name: patched.display_name, display_tags: patched.display_tags }
            : p
        )
      );
      toast.success("Display updated");
      setRenaming(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Rename failed";
      toast.error(String(msg));
    } finally {
      setRenameBusy(false);
    }
  };

  const deletePhoto = async (p) => {
    if (!window.confirm(`Delete ${p.display_name || p.filename}?`)) return;
    try {
      await Photos.remove(active.id, p.id);
      setPhotos((prev) => prev.filter((x) => x.id !== p.id));
      if (preview?.id === p.id) setPreview(null);
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (!active) {
    return (
      <div className="px-4 py-10 max-w-2xl mx-auto text-center" data-testid="gallery-empty-state">
        <p className="label-mono text-neutral-500 mb-2">03 / GALLERY</p>
        <h1 className="font-display text-3xl font-black mb-3">NO ACTIVE SESSION</h1>
        <p className="text-sm text-neutral-600 mb-6">
          Activate a session to view its photos.
        </p>
        <button onClick={() => nav("/sessions")} className="btn-flat" data-testid="gallery-go-sessions">
          Go to Sessions
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 max-w-5xl mx-auto page-fade" data-testid="gallery-page">
      <div className="flex flex-wrap items-end justify-between mb-4 gap-3">
        <div className="min-w-0">
          <p className="label-mono text-neutral-500">03 / GALLERY</p>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter break-all">
            {active.name.toUpperCase()}
          </h1>
          <p className="text-xs font-mono text-neutral-600 mt-1">
            {photos.length} PHOTOS · {allTags.length} TAG GROUPS{gpsCount > 0 && <span data-testid="gallery-gps-count"> · {gpsCount} GPS</span>}{segments.length > 0 && ` · ${segments.length} SEGMENTS`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <ImportPhotos session={active} onDone={reload} />
          {gpsCount > 0 && (
            <button onClick={() => nav(`/survey?session=${active.id}`)} className="btn-flat-outline" data-testid="gallery-survey-btn">
              <MapTrifold weight="bold" size={18} /> Survey
            </button>
          )}
          <button
            onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            className={selectMode ? "btn-flat" : "btn-flat-outline"}
            data-testid="gallery-select-mode-btn"
          >
            <ListChecks weight="bold" size={18} />
            {selectMode ? "Cancel" : "Select"}
          </button>
          <button
            onClick={() => nav("/camera")}
            className="btn-flat btn-shutter"
            data-testid="gallery-capture-btn"
          >
            <CameraIcon weight="bold" size={18} /> Capture
          </button>
        </div>
      </div>

      <TelemetryPanel session={active} onApplied={reload} />

      {segments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="gallery-segment-filter">
          <button onClick={() => setFilterSeg("ALL")} className={`chip ${filterSeg === "ALL" ? "chip-live" : ""}`} data-testid="seg-filter-all">ALL SEGMENTS</button>
          {segments.map((sg) => (
            <button key={sg} onClick={() => setFilterSeg(String(sg))} className={`chip ${filterSeg === String(sg) ? "chip-live" : ""}`} data-testid={`seg-filter-${sg}`}>
              SEG {String(sg + 1).padStart(2, "0")} · {photos.filter((p) => p.segment === sg).length}
            </button>
          ))}
          {photos.some((p) => p.segment == null) && (
            <button onClick={() => setFilterSeg("none")} className={`chip ${filterSeg === "none" ? "chip-live" : ""}`} data-testid="seg-filter-none">NO SEGMENT</button>
          )}
        </div>
      )}

      {/* Tag filter chips */}
      {photos.length > 0 && (
        <div
          className="flex flex-wrap gap-2 mb-6 pb-2 border-b-2 border-black"
          data-testid="gallery-tag-filter"
        >
          <button
            onClick={() => setFilterTag("ALL")}
            className={`chip transition-colors ${filterTag === "ALL" ? "chip-live" : ""}`}
            data-testid="tag-filter-all"
          >
            ALL · {photos.length}
          </button>
          {allTags.map((t) => {
            const count = photos.filter((p) => (p.tags || "untagged") === t).length;
            return (
              <button
                key={t}
                onClick={() => setFilterTag(t)}
                className={`chip transition-colors ${filterTag === t ? "chip-live" : ""}`}
                data-testid={`tag-filter-${slug(t)}`}
              >
                {t} · {count}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <p className="label-mono">Loading…</p>
      ) : photos.length === 0 ? (
        <div className="hairline-2 p-10 text-center" data-testid="gallery-zero-state">
          <p className="font-display text-2xl font-bold mb-2">NO PHOTOS YET</p>
          <p className="text-sm text-neutral-600 mb-6">Open the camera to capture your first frame, or import JPEGs from an SD card / drone above.</p>
          <button onClick={() => nav("/camera")} className="btn-flat" data-testid="gallery-open-camera-btn">
            Open Camera
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map(([tag, items]) => (
            <section key={tag} data-testid={`tag-group-${slug(tag)}`}>
              <div className="flex items-baseline justify-between border-b-2 border-black mb-3 pb-1 sticky-head">
                <h2 className="font-display text-xl font-bold tracking-tight truncate">{tag}</h2>
                <span className="label-mono shrink-0">{items.length} ITEMS</span>
              </div>
              <ul className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-0 border-2 border-black">
                {items.map((p) => {
                  const isSel = selected.has(p.id);
                  return (
                    <li
                    key={p.id}
                    className="relative aspect-square border border-black bg-neutral-100 group"
                    data-testid={`photo-cell-${p.id}`}
                  >
                    <button
                      onClick={() => (selectMode ? toggleSelect(p.id) : openPreview(p))}
                      className="absolute inset-0 w-full h-full"
                      aria-label={p.display_name || p.filename}
                      data-testid={`photo-open-${p.id}`}
                    >
                      <Thumb sessionId={active.id} photoId={p.id} alt={p.display_name || p.filename} />
                    </button>
                    {/* Synced status badge — top-left */}
                    <span
                      className={`absolute top-1 left-1 w-3 h-3 border-2 ${
                        p.synced_at
                          ? "bg-[#002FA7] border-white"
                          : "bg-white border-black"
                      }`}
                      title={p.synced_at ? `Synced ${p.synced_at}` : "Not yet synced"}
                      data-testid={`photo-sync-dot-${p.id}`}
                    />
                    {/* Display-override flag — when set, a yellow dot in top-right
                        signals "this photo has a UI override" without hiding canonical identity. */}
                    {p.gps?.lat != null && !selectMode && (
                      <span
                        className="absolute bottom-5 right-1 bg-[#002FA7] text-white p-0.5"
                        title={`${p.gps.lat.toFixed(5)}, ${p.gps.lon.toFixed(5)}${p.gps.alt != null ? ` · ${Math.round(p.gps.alt)} m` : ""}${p.segment != null ? ` · seg ${p.segment + 1}` : ""}`}
                        data-testid={`photo-gps-badge-${p.id}`}
                      >
                        <MapPin size={10} weight="bold" />
                      </span>
                    )}
                    {p.display_name && (
                      <span
                        className="absolute top-1 right-1 w-3 h-3 border-2 border-black bg-[#FFB000]"
                        title={`display: ${p.display_name}`}
                        data-testid={`photo-display-dot-${p.id}`}
                      />
                    )}
                    <span
                      className="absolute bottom-0 left-0 right-0 bg-black text-white text-[10px] font-mono px-1 py-0.5 truncate text-center pointer-events-none"
                      data-testid={`photo-label-${p.id}`}
                      title={p.display_name ? `${p.display_name} (orig: ${p.filename})` : p.filename}
                    >
                      #{padSeq(p.sequence)}
                      {p.display_name && (
                        <span className="ml-1 opacity-80">· {p.display_name}</span>
                      )}
                    </span>
                      {selectMode ? (
                        <button
                          onClick={() => toggleSelect(p.id)}
                          className={`absolute top-1 right-1 w-8 h-8 border-2 flex items-center justify-center transition-colors ${
                            isSel ? "bg-[#FF2A2A] border-[#FF2A2A] text-white" : "bg-white border-black text-black"
                          }`}
                          data-testid={`photo-select-${p.id}`}
                          aria-label="Select"
                        >
                          {isSel ? <CheckSquare size={16} weight="fill" /> : <Square size={16} weight="bold" />}
                        </button>
                      ) : (
                        <button
                          onClick={() => deletePhoto(p)}
                          className="absolute top-1 right-1 w-8 h-8 bg-white text-black border-2 border-black flex items-center justify-center active:scale-[0.9] sm:opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`photo-delete-${p.id}`}
                          aria-label="Delete"
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      {/* Bulk action bar */}
      {selectMode && (
        <div
          className="fixed inset-x-0 bottom-20 z-40 px-4 modal-in"
          data-testid="gallery-bulk-bar"
        >
          <div className="max-w-2xl mx-auto hairline-2 bg-black text-white p-3 flex items-center justify-between gap-2"
               style={{ boxShadow: "4px 4px 0 0 #FF2A2A" }}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="chip chip-blue shrink-0">{selected.size} SELECTED</span>
              <button
                onClick={selectAllVisible}
                className="label-mono underline opacity-90 hover:opacity-100"
                data-testid="select-all-visible-btn"
              >
                Select All
              </button>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                disabled={selected.size === 0 || bulkBusy}
                onClick={bulkDownload}
                className="px-3 py-2 bg-white text-black border-2 border-white font-bold text-xs uppercase tracking-wider disabled:opacity-40 active:scale-95 flex items-center gap-1.5"
                data-testid="bulk-download-btn"
              >
                <DownloadSimple weight="bold" size={16} />
                ZIP
              </button>
              <button
                disabled={selected.size === 0 || bulkBusy}
                onClick={bulkDelete}
                className="px-3 py-2 border-2 border-white font-bold text-xs uppercase tracking-wider disabled:opacity-40 active:scale-95 flex items-center gap-1.5"
                style={{ backgroundColor: "#FF2A2A" }}
                data-testid="bulk-delete-btn"
              >
                <Trash weight="bold" size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/95 text-white flex flex-col modal-in"
          data-testid="photo-preview-modal"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b-2 border-white/20 safe-top">
            <div className="min-w-0">
              <p className="label-mono opacity-70">FILE</p>
              <p className="font-mono truncate" data-testid="preview-canonical-filename">
                {preview.filename}
              </p>
              {(preview.display_name || preview.display_tags?.length > 0) && (
                <p className="font-mono text-[11px] text-[#FFB000] truncate" data-testid="preview-display-override">
                  display: {preview.display_name || preview.filename}
                  {preview.display_tags?.length > 0 && ` · [${preview.display_tags.join(", ")}]`}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setRenaming((v) => !v)}
                className="w-10 h-10 bg-white text-black flex items-center justify-center border-2 border-white"
                data-testid="preview-rename-toggle-btn"
                aria-label="Rename display"
                title="Rename display (UI metadata only)"
              >
                <PencilSimple size={18} weight="bold" />
              </button>
              <button
                onClick={closePreview}
                className="w-10 h-10 bg-white text-black flex items-center justify-center border-2 border-white"
                data-testid="preview-close-btn"
                aria-label="Close preview"
              >
                <X size={20} weight="bold" />
              </button>
            </div>
          </div>

          {renaming && (
            <div
              className="px-4 py-3 border-b-2 border-white/20 space-y-2"
              data-testid="preview-rename-panel"
            >
              <p className="font-mono text-[10px] opacity-70">
                UI metadata only — original filename / tags are preserved
              </p>
              <input
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                placeholder={preview.filename}
                className="w-full px-3 py-2 font-mono text-sm text-black bg-white border-2 border-white"
                data-testid="preview-display-name-input"
              />
              <input
                value={renameTags}
                onChange={(e) => setRenameTags(e.target.value)}
                placeholder={preview.tags || "comma,separated,tags"}
                className="w-full px-3 py-2 font-mono text-sm text-black bg-white border-2 border-white"
                data-testid="preview-display-tags-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={saveRename}
                  disabled={renameBusy}
                  className="btn-flat bg-white text-black border-white"
                  data-testid="preview-save-display-btn"
                >
                  <FloppyDisk size={16} weight="bold" />
                  {renameBusy ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => {
                    setRenameName("");
                    setRenameTags("");
                  }}
                  className="btn-flat-outline border-white text-white"
                  data-testid="preview-clear-display-btn"
                  title="Empty fields and Save to clear overrides"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
          <div className="flex-1 flex items-center justify-center overflow-auto p-4">
            <img
              src={photoSrc(preview)}
              alt={preview.filename}
              className="max-w-full max-h-full object-contain border-2 border-white"
              data-testid="preview-image"
            />
          </div>
          <div className="px-4 py-3 border-t-2 border-white/20 grid grid-cols-2 gap-3 safe-bottom">
            {preview.image_data ? (
              <a
                href={preview.image_data}
                download={preview.filename}
                className="btn-flat-outline bg-white text-black"
                data-testid="preview-download-btn"
              >
                <ArrowSquareOut weight="bold" size={18} /> Download
              </a>
            ) : (
              <button
                onClick={async () => saveBlob(await fetchPhotoBlob(preview.id), preview.filename)}
                className="btn-flat-outline bg-white text-black"
                data-testid="preview-download-btn"
              >
                <ArrowSquareOut weight="bold" size={18} /> Download original
              </button>
            )}
            <button
              onClick={() => deletePhoto(preview)}
              className="btn-flat btn-shutter"
              data-testid="preview-delete-btn"
            >
              <Trash weight="bold" size={18} /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function slug(s) {
  return (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function Thumb({ sessionId, photoId, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Photos.get(sessionId, photoId)
      .then((p) => {
        if (!cancelled) setUrl(photoSrc(p, "preview"));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sessionId, photoId]);
  if (!url) return <div className="w-full h-full bg-neutral-200" />;
  return <img src={url} alt={alt} className="w-full h-full object-cover" />;
}
